import { Entity } from '@backstage/catalog-model';
import { JsonArray, JsonObject, JsonValue } from '@backstage/types';
import { v5 as uuidv5 } from 'uuid';

import { TenantSpec } from '../types';

const readObject = (value: JsonValue | undefined, path: string): JsonObject => {
  if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
    return value;
  }

  throw new TypeError(`Invalid KaaP tenant: ${path} must be an object`);
};

const readString = (value: JsonValue | undefined, path: string): string => {
  if (typeof value === 'string') {
    return value;
  }

  throw new TypeError(`Invalid KaaP tenant: ${path} must be a string`);
};

const readNumber = (value: JsonValue | undefined, path: string): number => {
  const parsed =
    typeof value === 'string' && value.trim() !== '' ? Number(value) : value;
  if (typeof parsed === 'number' && Number.isFinite(parsed)) {
    return parsed;
  }

  throw new TypeError(`Invalid KaaP tenant: ${path} must be a finite number`);
};

const readStringArray = (
  value: JsonValue | undefined,
  path: string,
): string[] => {
  if (value === undefined || value === null) {
    return [];
  }
  if (Array.isArray(value)) {
    return value.map((item, index) => readString(item, `${path}[${index}]`));
  }

  throw new TypeError(
    `Invalid KaaP tenant: ${path} must be an array of strings`,
  );
};

type TenantClusters = NonNullable<TenantSpec['clusters']>;

/** Tenants registered before multi-cluster support only carry `clusterId`. */
export const clustersFromId = (
  clusterId?: string,
  nodeGroups: string[] = [],
): TenantClusters =>
  clusterId
    ? [
        {
          id: clusterId,
          name: clusterId.split('/')[1] ?? clusterId,
          nodeGroups,
        },
      ]
    : [];

const readClusters = (
  value: JsonValue | undefined,
  clusterId: string,
  nodeGroups: string[],
): TenantClusters => {
  if (value === undefined || value === null) {
    return clustersFromId(clusterId, nodeGroups);
  }
  if (!Array.isArray(value)) {
    throw new TypeError(
      'Invalid KaaP tenant: spec.tenant.clusters must be an array',
    );
  }

  return value.map((item, index) => {
    const path = `spec.tenant.clusters[${index}]`;
    const cluster = readObject(item, path);
    return {
      id: readString(cluster.id, `${path}.id`),
      name: readString(cluster.name, `${path}.name`),
      nodeGroups: readStringArray(cluster.nodeGroups, `${path}.nodeGroups`),
    };
  });
};

function readBoolean(
  value: string | number | boolean | JsonObject | JsonArray | null | undefined,
  path: string,
): boolean {
  if (typeof value === 'boolean') {
    return value;
  }

  if (value === null || value === undefined) {
    return false;
  }

  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (normalized === 'true') {
      return true;
    }
    if (normalized === 'false') {
      return false;
    }
  }

  throw new TypeError(`Invalid KaaP tenant: ${path} must be a boolean`);
}

/**
 * Reads a Resource of type `kaap-tenant` as a validated TenantSpec.
 * Numeric IDs and string quotas from YAML are normalized without changing units.
 * Absent/null lists, cabInform, applicationId and slackChannel default to [],
 * false and empty strings respectively. Other missing or invalid fields throw
 * a TypeError identifying the field, without including its value.
 */
export const toKaaPTenant = (entity: Entity): TenantSpec => {
  if (
    entity.kind.toLowerCase() !== 'resource' ||
    entity.spec?.type !== 'kaap-tenant'
  ) {
    throw new TypeError(
      'Expected a Resource entity with spec.type=kaap-tenant',
    );
  }

  const tenant = readObject(entity.spec.tenant, 'spec.tenant');
  const quota = readObject(
    tenant.tenantResourceQuota,
    'spec.tenant.tenantResourceQuota',
  );
  const cabInform = tenant.cabInform ?? false;
  if (typeof cabInform !== 'boolean') {
    throw new TypeError(
      'Invalid KaaP tenant: spec.tenant.cabInform must be a boolean',
    );
  }
  const clusterId = readString(tenant.clusterId, 'spec.tenant.clusterId');
  // Tenants registered before `clusters` existed keep their node groups at the
  // tenant level; from then on they live on each cluster.
  const clusters = readClusters(
    tenant.clusters,
    clusterId,
    readStringArray(tenant.nodeGroups, 'spec.tenant.nodeGroups'),
  );
  const id =
    typeof tenant.id === 'number'
      ? String(readNumber(tenant.id, 'spec.tenant.id'))
      : readString(tenant.id, 'spec.tenant.id');

  // `pimGroupId: null` means PIM is disabled. Entries written before
  // `pimGroupId` existed have no such key and carry `pimEnabled` instead.
  const pimEnabled =
    tenant.pimGroupId === undefined
      ? readBoolean(tenant.pimEnabled, 'spec.tenant.pimEnabled')
      : tenant.pimGroupId !== null;

  return {
    id,
    name: readString(tenant.name, 'spec.tenant.name'),
    stage: readString(tenant.stage, 'spec.tenant.stage'),
    tier: readString(tenant.tier, 'spec.tenant.tier'),
    cabInform,
    ownerObjectId: readString(
      tenant.ownerObjectId,
      'spec.tenant.ownerObjectId',
    ),
    securityObjectIds: readStringArray(
      tenant.securityObjectIds,
      'spec.tenant.securityObjectIds',
    ),
    applicationId: readString(
      tenant.applicationId ?? '',
      'spec.tenant.applicationId',
    ),
    slackChannel: readString(
      tenant.slackChannel ?? '',
      'spec.tenant.slackChannel',
    ),
    clusterId,
    clusters,
    nodeGroups: Array.from(new Set(clusters.flatMap(c => c.nodeGroups))),
    namespaceQuota: readNumber(
      tenant.namespaceQuota,
      'spec.tenant.namespaceQuota',
    ),
    pimEnabled,
    pimGroupId: pimEnabled
      ? readString(tenant.pimGroupId ?? '', 'spec.tenant.pimGroupId')
      : '',
    pimStatus: readString(tenant.pimStatus ?? '', 'spec.tenant.pimStatus'),
    tenantResourceQuota: {
      cpuRequest: readNumber(
        quota.cpuRequest,
        'spec.tenant.tenantResourceQuota.cpuRequest',
      ),
      cpuLimit: readNumber(
        quota.cpuLimit,
        'spec.tenant.tenantResourceQuota.cpuLimit',
      ),
      memoryRequest: readNumber(
        quota.memoryRequest,
        'spec.tenant.tenantResourceQuota.memoryRequest',
      ),
      memoryLimit: readNumber(
        quota.memoryLimit,
        'spec.tenant.tenantResourceQuota.memoryLimit',
      ),
    },
  };
};

// RFC 4122 DNS namespace, same Terraform's uuidv5("dns", ...) in kaap-iac
const DNS_NAMESPACE = '6ba7b810-9dad-11d1-80b4-00c04fd430c8';

export function getClusterHostedZone(
  clusterName: string,
  tier: string,
  kaapHostedZoneName: string,
): string {
  const dnsId = `${clusterName}.${tier}.${kaapHostedZoneName}`;
  const uid = uuidv5(dnsId, DNS_NAMESPACE).split('-')[0];
  return `${uid}.${tier}.${kaapHostedZoneName}`;
}
